<template>
    <div class="space-y-4">
        <h1>Alignment and side variations</h1>

        <ul class="text-sm">
            <li><b>Side</b>: refers to the position of the trigger relative to the popover</li>
            <li><b>Align</b>: refers the boundary alignment of the popover content relative to the trigger</li>
        </ul>
    </div>
    <div class="mt-6 space-y-14">
        <div
            v-for="align in alignments"
            :key="align"
            class="grid grid-cols-4 gap-8 text-center"
        >
            <div
                v-for="side in sides"
                :key="side"
                class="space-y-2"
            >
                <p class="text-heading-sm">{{ side }} {{ align }}</p>
                <DsPopover>
                    <DsPopoverTrigger>
                        <DsButton
                            size="sm"
                            variant="secondary"
                        >
                            Open
                        </DsButton>
                    </DsPopoverTrigger>
                    <DsPopoverContent
                        :align
                        class="p-2"
                        :side
                    >
                        <div class="w-96 space-y-2">
                            <b>Por enquanto - Cássia Eller</b>
                            <p class="w-full text-content-md">Mudaram as estações</p>
                            <p class="w-full text-content-md">Nada mudou</p>
                            <p class="w-full text-content-md">Mas eu sei que alguma coisa aconteceu</p>
                            <p class="w-full text-content-md">Tá tudo assim, tão diferente</p>
                        </div>
                    </DsPopoverContent>
                </DsPopover>
            </div>
        </div>
        <div>
            <h2>Collision detection</h2>
            <div>
                <p>This trigger should render at the left side of the popover but it's shifted due to lack of space</p>
                Add some left space:
                <input
                    v-model="leftSpace"
                    class="w-36"
                    max="1440"
                    min="0"
                    type="range"
                />
                <small>{{ leftSpace }}px</small>
                <br />
                <div
                    ref="popoverTestRow"
                    class="border-2 border-ds-primary-500 p-4"
                    :style="{ paddingLeft: `${leftSpace}px !important` }"
                >
                    <DsPopover>
                        <DsPopoverTrigger>
                            <DsButton
                                size="sm"
                                variant="secondary"
                            >
                                Open
                            </DsButton>
                        </DsPopoverTrigger>
                        <DsPopoverContent
                            align="start"
                            class="p-2"
                            side="left"
                        >
                            <div class="grid w-[620px] grid-cols-2 space-y-2">
                                <div>
                                    <b>Garota de Ipanema - Tom Jobim</b>
                                    <p class="w-full text-content-md">Olha que coisa mais linda, mais cheia de graça</p>
                                    <p class="w-full text-content-md">É ela, menina, que vem e que passa</p>
                                    <p class="w-full text-content-md">Num doce balanço a caminho do mar</p>
                                </div>
                                <div>
                                    <iframe
                                        allow="
                                            accelerometer;
                                            autoplay;
                                            clipboard-write;
                                            encrypted-media;
                                            gyroscope;
                                            picture-in-picture;
                                            web-share;
                                        "
                                        frameborder="0"
                                        height="140"
                                        referrerpolicy="strict-origin-when-cross-origin"
                                        src="https://www.youtube.com/embed/NldPFVKYmiw?si=Thc1e-u_L53pYH81"
                                        title="YouTube video player"
                                        width="300"
                                    ></iframe>
                                </div>
                            </div>
                        </DsPopoverContent>
                    </DsPopover>
                </div>
            </div>
        </div>

        <div>
            <h2>External open/close control</h2>
            <div class="flex w-1/3 flex-col gap-4">
                <DsButton
                    size="sm"
                    variant="secondary"
                    @click="openModel = true"
                >
                    Open
                </DsButton>
                <small>Open state:{{ openModel }}</small>

                <DsPopover v-model:open="openModel">
                    <DsPopoverTrigger>
                        <div>
                            Trigger
                            <DsIcon
                                icon="chevron-down"
                                size="sm"
                            />
                        </div>
                    </DsPopoverTrigger>
                    <DsPopoverContent class="p-2">
                        <div class="w-96 space-y-2">
                            <b>Chega de Saudade - João Gilberto</b>
                            <p class="w-full text-content-md">Vai, minha tristeza, e diz a ela</p>
                            <p class="w-full text-content-md">Que sem ela não pode ser</p>
                            <p class="w-full text-content-md">Diz-lhe numa prece que ela regresse</p>
                            <p class="w-full text-content-md">Porque eu não posso mais sofrer</p>
                            <iframe
                                allow="
                                    accelerometer;
                                    autoplay;
                                    clipboard-write;
                                    encrypted-media;
                                    gyroscope;
                                    picture-in-picture;
                                    web-share;
                                "
                                frameborder="0"
                                height="216"
                                referrerpolicy="strict-origin-when-cross-origin"
                                src="https://www.youtube.com/embed/wJk1v2MowHo?si=XC4MM8FcVzIT5h7o"
                                title="YouTube video player"
                                width="384"
                            ></iframe>
                        </div>
                    </DsPopoverContent>
                </DsPopover>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import DsPopover from '@/components/design-system/popover/DsPopover.vue';
import DsPopoverContent from '@/components/design-system/popover/DsPopoverContent.vue';
import DsPopoverTrigger from '@/components/design-system/popover/DsPopoverTrigger.vue';
import { ref, useTemplateRef } from 'vue';

const sides = ['right', 'top', 'bottom', 'left'] as const;
const alignments = ['start', 'center', 'end'] as const;

const leftSpace = ref(0);
const openModel = ref(false);
const popoverTestRow = useTemplateRef<HTMLDivElement | null>('popoverTestRow');
</script>
